<template>
  <div class="lb-calendar">
    <div class="el-calendar">
      <div class="el-calendar__header">
        <div class="el-calendar__title">
          {{ i18nDate }}
        </div>
        <div
          class="el-calendar__button-group"
          v-if="validatedRange.length === 0">
          <lb-button
            type="text"
            size="mini"
            @click="selectDate('today')">
            {{ t('el.datepicker.today') }}
          </lb-button>
        </div>
      </div>
      <div
        class="el-calendar__body"
        v-if="validatedRange.length === 0"
        key="no-range">
        <date-table
          :date="date"
          :selected-day="realSelectedDay"
          :first-day-of-week="realFirstDayOfWeek"
          @prevMonth="selectDate('prev-month')"
          @nextMonth="selectDate('next-month')"
          @drop="handleDrop"
          @pick="pickDay" />
      </div>
      <div
        v-else
        class="el-calendar__body"
        key="has-range">
        <date-table
          v-for="(range, index) in validatedRange"
          :key="index"
          :date="range[0]"
          @prevMonth="selectDate('prev-month')"
          @nextMonth="selectDate('next-month')"
          :selected-day="realSelectedDay"
          :range="range"
          :hide-header="index !== 0"
          @drop="handleDrop"
          @pick="pickDay" />
      </div>
    </div>
  </div>
</template>

<script>
import Locale from 'element-ui/src/mixins/locale';
import fecha from 'element-ui/src/utils/date';
import DateTable from './date-table';
import { validateRangeInOneMonth } from 'element-ui/src/utils/date-util';

const validTypes = ['prev-month', 'today', 'next-month'];
const oneDay = 86400000;

export default {
  name: 'lb-calendar',

  mixins: [Locale],

  components: {
    DateTable
  },

  props: {
    value: [Date, String, Number],
    range: {
      type: Array,
      validator(range) {
        if (Array.isArray(range)) {
          return range.length === 2 && range.every(
            item => typeof item === 'string' ||
            typeof item === 'number' ||
            item instanceof Date);
        } else {
          return true;
        }
      }
    },
    firstDayOfWeek: {
      type: Number,
      default: 1
    }
  },

  provide() {
    return {
      elCalendar: this
    };
  },

  methods: {
    handleDrop(date) {
      this.$emit('drop', date)
    },

    pickDay(day) {
      this.realSelectedDay = day;
      this.$emit('pick', day)
    },

    selectDate(type) {
      if (validTypes.indexOf(type) === -1) {
        throw new Error(`invalid type ${type}`);
      }
      let day = '';
      if (type === 'prev-month') {
        day = `${this.prevMonthDatePrefix}-01`;
      } else if (type === 'next-month') {
        day = `${this.nextMonthDatePrefix}-01`;
      } else {
        day = this.formatedToday;
      }

      if (day === this.formatedDate) return;
      this.pickDay(day);
    },

    toDate(val) {
      if (!val) {
        throw new Error('invalid val');
      }
      return val instanceof Date ? val : new Date(val);
    }
  },

  computed: {
    prevMonthDatePrefix() {
      const temp = new Date(this.date.getTime());
      temp.setDate(0);
      return fecha.format(temp, 'yyyy-MM');
    },

    curMonthDatePrefix() {
      return fecha.format(this.date, 'yyyy-MM');
    },

    nextMonthDatePrefix() {
      const temp = new Date(this.date.getFullYear(), this.date.getMonth() + 1, 1);
      return fecha.format(temp, 'yyyy-MM');
    },

    formatedDate() {
      return fecha.format(this.date, 'yyyy-MM-dd');
    },

    i18nDate() {
      const year = this.date.getFullYear();
      const month = this.date.getMonth() + 1;
      return `${year} ${this.t('el.datepicker.year')} ${this.t('el.datepicker.month' + month)}`;
    },

    formatedToday() {
      return fecha.format(this.now, 'yyyy-MM-dd');
    },

    realSelectedDay: {
      get() {
        if (!this.value) return this.selectedDay;
        return this.formatedDate;
      },
      set(val) {
        this.selectedDay = val;
        const date = new Date(val);
        this.$emit('input', date);
      }
    },

    date() {
      if (!this.value) {
        if (this.realSelectedDay) {
          return new Date(this.selectedDay);
        } else if (this.validatedRange.length) {
          return this.validatedRange[0][0];
        }
        return this.now;
      } else {
        return this.toDate(this.value);
      }
    },

    // if range is valid, we get a two-digit array
    validatedRange() {
      let range = this.range;
      if (!range) return [];
      const expetedMap = {
        0: {
          value: 1,
          message: 'start of range should be Monday.'
        },
        1: {
          value: 0,
          message: 'end of range should be Sunday.'
        }
      };
      range = range.reduce((prev, val, index) => {
        const date = this.toDate(val);
        if (date.getDay() !== expetedMap[index].value) {
          console.warn('[ElementCalendar]', expetedMap[index].message, ' invalid range will be ignored');
        } else {
          prev = prev.concat(date);
        }
        return prev;
      }, []);
      if (range.length === 2) {
        const [start, end] = range;
        if (start > end) {
          console.warn('[ElementCalendar]end time should be greater than start time');
          return [];
        }
        // start time and end time in one month
        if (validateRangeInOneMonth(start, end)) {
          return [
            [start, end]
          ];
        }
        const data = [];
        let startDay = new Date(start.getFullYear(), start.getMonth() + 1, 1);
        const lastDay = this.toDate(startDay.getTime() - oneDay);
        if (!validateRangeInOneMonth(startDay, end)) {
          console.warn('[ElementCalendar]start time and end time interval must not exceed two months');
          return [];
        }
        data.push([
          start,
          lastDay
        ]);
        let interval = startDay.getDay();
        interval = interval <= 1 ? Math.abs(interval - 1) : (8 - interval);
        startDay = this.toDate(startDay.getTime() + interval * oneDay);
        if (startDay.getDate() < end.getDate()) {
          data.push([
            startDay,
            end
          ]);
        }
        return data;
      }
      return [];
    },

    realFirstDayOfWeek() {
      if (this.firstDayOfWeek < 1 || this.firstDayOfWeek > 6) {
        return 0;
      }
      return Math.floor(this.firstDayOfWeek);
    }
  },

  data() {
    return {
      selectedDay: '',
      now: new Date()
    };
  }
};
</script>

<style lang="scss">
  @import "../../styles/common/var";
  @import "../../styles/mixins/mixins";

  @include lb(calendar){
    height: 100%;
    .el-calendar{
      height: 100%;
    }
    .el-calendar-table .el-calendar-day{
      position: relative;
      padding: 5px;
    }
    .el-calendar-table .el-calendar-day:hover{
      background-color: #F0F0FC;
    }
    .el-calendar-table td.is-selected{
      background-color: #F0F0FC;
    }
    .el-backtop{
      color: #2e6e75;
      font-weight: 500;
    }
    .el-calendar-table td.is-today{
      color: #2e6e75;
      font-weight: 500;
    }
    .el-calendar-table thead th{
      padding: 5px 0;
      border-right: 1px solid #EBEEF5;
      &:nth-child(1){
        border-left: 1px solid #EBEEF5;
      }
    }
    .table-menu{
      display: flex;
      font-size: 14px;
      font-weight: 500;
      align-items: center;
      justify-content: space-around;
      background: url("data:image/svg+xml;base64,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") no-repeat center / cover;
      height: 50px;
      span{
        margin: 0 15px;
      }
      i{
        cursor: pointer;
        &:hover{
          color: #2e6e75;
        }
      }
    }
  }
</style>
